import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Upload, FileText, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function BulkUploadDialog({ open, onClose, onDone, endpoint, entityName, templateFields }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error('Only CSV files supported'); return; }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a CSV file first'); return; }
    setUploading(true);
    try {
      const text = await file.text();
      const { data } = await axios.post(`${API}${endpoint}`, { csv_text: text });
      setResult(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} ${entityName.toLowerCase()}(s)`);
        if (onDone) onDone();
      }
      if (data.errors > 0) toast.warning(`${data.errors} row(s) had errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    const csv = templateFields.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${entityName.toLowerCase()}_template.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setFile(null); setResult(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-blue-500" /> Bulk Import {entityName}</DialogTitle>
          <DialogDescription>Upload a CSV file to import multiple {entityName.toLowerCase()} at once</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div>
              <p className="text-xs font-semibold text-blue-700">Download CSV Template</p>
              <p className="text-[10px] text-blue-500">Use this template for correct column headers</p>
            </div>
            <Button size="sm" variant="outline" onClick={downloadTemplate} className="text-xs gap-1 h-7" data-testid="download-template-btn">
              <Download className="h-3 w-3" /> Template
            </Button>
          </div>

          {/* File Drop Zone */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => inputRef.current?.click()}
            data-testid="csv-dropzone"
          >
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <span className="text-sm font-medium">{file.name}</span>
                <Badge className="bg-blue-100 text-blue-700 text-[10px]">{(file.size / 1024).toFixed(1)} KB</Badge>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Click to select a CSV file</p>
                <p className="text-[10px] text-gray-400 mt-1">Max 5,000 rows per import</p>
              </div>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="p-3 rounded-lg border space-y-2" style={{ background: result.errors > 0 ? '#fffbeb' : '#f0fdf4', borderColor: result.errors > 0 ? '#fde68a' : '#bbf7d0' }}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">{result.imported} imported successfully</span>
              </div>
              {result.errors > 0 && (
                <div>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-700">{result.errors} row(s) failed</span>
                  </div>
                  <div className="mt-1 max-h-24 overflow-y-auto">
                    {(result.error_details || []).slice(0, 10).map((e, i) => (
                      <p key={i} className="text-[10px] text-amber-600">Row {e.row}: {e.reason}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setFile(null); setResult(null); }}>Close</Button>
          <Button onClick={handleUpload} disabled={!file || uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="import-btn">
            {uploading ? 'Importing...' : `Import ${entityName}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
